import { type FC, useMemo } from "react";
import { Table } from "react-bootstrap";
import { type PostDto, PostStatus, postLabel } from "shared";
import { usePostsActions } from "../calendar/usePosts";
import { Modal, useModal } from "../common/modal/Modal";
import { PlatformIcon } from "../common/PlatformIcon";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";

interface ModalBulkApproveProps {
	posts: PostDto[];
}

export const ModalBulkApprove: FC<ModalBulkApproveProps> = ({ posts }) => {
	const { close } = useModal();
	const { bulkApprovePosts } = usePostsActions();
	const company = useCompany();

	const approvablePosts = useMemo(
		() =>
			posts.filter(
				(p) =>
					p.status === PostStatus.PENDING_APPROVAL ||
					(company.requirePostApproval && p.status === PostStatus.DRAFT),
			),
		[posts, company.requirePostApproval],
	);

	const handleApproveAll = async () => {
		if (approvablePosts.length === 0) return;
		await bulkApprovePosts.mutateAsync(approvablePosts.map((p) => p.id));
		close();
	};

	return (
		<Modal size="lg">
			<h5 className="mb-3">Approve Posts</h5>

			{approvablePosts.length === 0 ? (
				<p className="text-muted">No posts awaiting approval.</p>
			) : (
				<>
					<p className="text-muted mb-3">
						{approvablePosts.length} post
						{approvablePosts.length !== 1 ? "s" : ""} awaiting approval:
					</p>

					<Table size="sm" className="mb-4">
						<thead>
							<tr>
								<th>Platform</th>
								<th>Subject</th>
								<th>Status</th>
							</tr>
						</thead>
						<tbody>
							{approvablePosts.map((post) => (
								<tr key={post.id}>
									<td>
										<PlatformIcon value={post.platform} />
									</td>
									<td>{postLabel(post)}</td>
									<td>
										{post.status === PostStatus.PENDING_APPROVAL
											? "Pending"
											: "Draft"}
									</td>
								</tr>
							))}
						</tbody>
					</Table>

					<Button
						onClick={handleApproveAll}
						loading={bulkApprovePosts.isPending}
						icon="check_circle"
						full
					>
						Approve All ({approvablePosts.length})
					</Button>
				</>
			)}

			<Button variant="ghost" onClick={close} className="mt-2" full>
				Cancel
			</Button>
		</Modal>
	);
};
